import {cx} from '@emotion/css';
import React, {ReactElement} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';

import {ReactComponent as FoAndTideFooterLogoPlainWhite} from '@fo/shared-images/logo/FOxTide_logo_plain_white.svg';
import {ReactComponent as TwitterIcon} from '@fo/shared-images/icons/social/twitter.svg';
import {ReactComponent as LinkedInIcon} from '@fo/shared-images/icons/social/linkedIn.svg';
import {ReactComponent as FacebookIcon} from '@fo/shared-images/icons/social/facebook.svg';
import {ReactComponent as InstagramIcon} from '@fo/shared-images/icons/social/instagram.svg';
import {ReactComponent as VimeoIcon} from '@fo/shared-images/icons/social/vimeo.svg';

import LayoutSection from '@fo/components/DesignSystem/Layout/LayoutSection/LayoutSection';
import DynamicLink from '@fo/components/DynamicLink/DynamicLink';
import {getInTouchLinks, getLegalLinks, getMenuLinks} from '@fo/components/DesignSystem/Footer/FooterLinks';

import styles from './index.styles';

function FooterV2(): ReactElement {
  const {formatMessage} = useIntl();

  return (
    <LayoutSection wide className={cx(styles.footerV2)}>
      <div className={cx(styles.linksWrapper)}>
        <ul className={cx(styles.linkContainer)}>
          <li>
            <h5 className={cx(styles.linkHeader)}>
              <FormattedMessage id="footer.column.menu.header" />
            </h5>
          </li>
          {getMenuLinks(formatMessage).map((link) => (
            <li key={link.label}>
              <DynamicLink className="unstyledLink" href={link.href}>
                {link.label}
              </DynamicLink>
            </li>
          ))}
        </ul>
        <ul className={cx(styles.linkContainer)}>
          <li>
            <h5 className={cx(styles.linkHeader)}>
              <FormattedMessage id="footer.column.legal.header" />
            </h5>
          </li>
          {getLegalLinks(formatMessage).map((link) => (
            <li key={link.label}>
              <DynamicLink className="unstyledLink" href={link.href}>
                {link.label}
              </DynamicLink>
            </li>
          ))}
        </ul>
        <ul className={cx(styles.linkContainer)}>
          <li>
            <h5 className={cx(styles.linkHeader)}>
              <FormattedMessage id="footer.column.getInTouch.header" />
            </h5>
          </li>
          {getInTouchLinks(formatMessage).map((link) => (
            <li key={link.label}>
              <DynamicLink className="unstyledLink" href={link.href}>
                {link.label}
              </DynamicLink>
            </li>
          ))}
          <li>
            <ul className={cx(styles.socialIconContainer)}>
              <li>
                <DynamicLink
                  blank
                  href={formatMessage({id: 'footer.getInTouch.linkedIn.href'})}
                  className={cx('unstyledLink')}
                  testId="ui.footer.social.linkedIn"
                >
                  <LinkedInIcon
                    className={cx(styles.socialIcon)}
                    title={formatMessage({id: 'footer.getInTouch.linkedIn.alt'})}
                  />
                </DynamicLink>
              </li>
              <li>
                <DynamicLink
                  blank
                  href={formatMessage({id: 'footer.getInTouch.facebook.href'})}
                  className={cx('unstyledLink')}
                  testId="ui.footer.social.facebook"
                >
                  <FacebookIcon
                    className={cx(styles.socialIcon)}
                    title={formatMessage({id: 'footer.getInTouch.facebook.alt'})}
                  />
                </DynamicLink>
              </li>
              <li>
                <DynamicLink
                  blank
                  href={formatMessage({id: 'footer.getInTouch.twitter.href'})}
                  className={cx('unstyledLink')}
                  testId="ui.footer.social.twitter"
                >
                  <TwitterIcon
                    className={cx(styles.socialIcon)}
                    title={formatMessage({id: 'footer.getInTouch.twitter.alt'})}
                  />
                </DynamicLink>
              </li>
              <li>
                <DynamicLink
                  blank
                  href={formatMessage({id: 'footer.getInTouch.instagram.href'})}
                  className={cx('unstyledLink')}
                  testId="ui.footer.social.instagram"
                >
                  <InstagramIcon
                    className={cx(styles.socialIcon)}
                    title={formatMessage({id: 'footer.getInTouch.instagram.alt'})}
                  />
                </DynamicLink>
              </li>
              <li>
                <DynamicLink
                  blank
                  href={formatMessage({id: 'footer.getInTouch.vimeo.href'})}
                  className={cx('unstyledLink')}
                  testId="ui.footer.social.vimeo"
                >
                  <VimeoIcon
                    className={cx(styles.socialIcon)}
                    title={formatMessage({id: 'footer.getInTouch.vimeo.alt'})}
                  />
                </DynamicLink>
              </li>
            </ul>
          </li>
        </ul>
      </div>
      <div className={cx(styles.logoDisclaimerContainer)}>
        <FoAndTideFooterLogoPlainWhite
          className={cx(styles.logo)}
          title={formatMessage({id: 'footer.column.image.alt'})}
        />
        <p className={cx(styles.disclaimer)}>
          <FormattedMessage id="footer.column.paragraph.disclaimer.v3" />
        </p>
      </div>
    </LayoutSection>
  );
}

export default FooterV2;
